import {faTrash} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Input} from 'antd';

import {FlagWithTooltip} from '../../../../../../../../component/languages-popup/flag/flag-with-tooltip';
import {useLocale} from '../../../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../../../provider/locale/localization';
import {CompanyKeyEnum} from '../../../../../../../../service/company-v2/company-const';
import {useFormRules} from '../../../../../../../../service/form/form-rules-hook';
import {Form} from '../../../../../../../../typings/antd';
import {classNames} from '../../../../../../../../util/css';
import {PushedNameInput} from '../pushed-name-input/pushed-name-input';
import {ShortNameInput} from '../short-name-input/short-name-input';

import * as styles from './name-input.scss';

type PropsType = {
    index: number;
    fieldKey?: number;
    fieldName: number;
    basePath: [CompanyKeyEnum.Names] | [CompanyKeyEnum.ExtraNames, number, 'names'];
    supportsPushedName: boolean;
    supportsShortName: boolean;
    allowShortNameLongerThanName: boolean;
    supportsNameWithoutLanguage: boolean;
    remove: (index: number | Array<number>) => void;
    openRemovalModal?: (() => void) | null;
};

export function NameInput(props: PropsType): JSX.Element {
    const {
        index,
        fieldKey,
        fieldName,
        basePath,
        supportsPushedName,
        supportsShortName,
        allowShortNameLongerThanName,
        supportsNameWithoutLanguage,
        remove,
        openRemovalModal,
    } = props;

    const {requiredFieldRule, whitespaceFieldRule} = useFormRules();
    // The base name of the block carries the column labels, the languages added to it repeat its columns
    const showLabels = index === 0 && supportsNameWithoutLanguage;

    const {getLocalizedString} = useLocale();

    const namePath = [...basePath, index, 'name'];
    const language = Form.useWatch([...basePath, index, 'lang']);

    function renderRemoveButton(): JSX.Element | null {
        // A name of a language is removed on its own. The one without a language is the base name of its block —
        // the company name itself, or the settings of a catalog, which have their own removal control below the list
        if (showLabels && !language) {
            return null;
        }

        return (
            <Button
                className={styles.name_input__remove_button}
                onClick={() => (openRemovalModal ? openRemovalModal() : remove(fieldName))}
                type="text"
            >
                <FontAwesomeIcon className={styles.name_input__remove_button_image} icon={faTrash} />
            </Button>
        );
    }

    function renderSecondField(): JSX.Element | null {
        if (supportsShortName) {
            return (
                <ShortNameInput
                    allowLongerThanName={allowShortNameLongerThanName}
                    fieldName={fieldName}
                    showLabel={showLabels}
                />
            );
        }

        if (supportsPushedName) {
            return (
                <Form.Item dependencies={[namePath]} noStyle>
                    {({getFieldValue}) => (
                        <PushedNameInput
                            name={String(getFieldValue(namePath) ?? '')}
                            pushedName={String(getFieldValue([...basePath, index, 'pushedName']) ?? '')}
                            showLabel={showLabels}
                        />
                    )}
                </Form.Item>
            );
        }

        return null;
    }

    const secondField = renderSecondField();

    return (
        <div className={classNames(styles.name_input__row, {[styles.name_input__row_labeled]: showLabels})}>
            <div className={styles.name_input__column}>
                {language ? (
                    <div className={styles.name_input__flag}>
                        <Form.Item name={[fieldName, 'lang']} noStyle>
                            <FlagWithTooltip />
                        </Form.Item>
                    </div>
                ) : null}

                <Form.Item
                    fieldKey={fieldKey}
                    label={showLabels ? <Locale stringKey="COMPANY_FORM__NAMES__COMPANY_NAME__LABEL" /> : null}
                    name={[fieldName, 'name']}
                    rules={[requiredFieldRule, whitespaceFieldRule]}
                >
                    <Input
                        placeholder={getLocalizedString('COMPANY_FORM__NAMES__COMPANY_NAME__PLACEHOLDER')}
                        size={showLabels ? 'large' : 'middle'}
                    />
                </Form.Item>

                {secondField ? null : renderRemoveButton()}
            </div>

            {secondField ? (
                <div className={styles.name_input__column}>
                    {secondField}
                    {renderRemoveButton()}
                </div>
            ) : null}
        </div>
    );
}
